import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "@/components/button";
import { useMutation } from "react-query";
import { bulkUpdateTags } from "@/services/patient-tags";
import { useUpdateActionState } from "@/hooks/tasks";
import { useTagUndoStore } from "@/stores/tag-undo";
import { usePatientTagsStore } from "@/stores/patient-tags";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { formatTagValue, applyTagUpdate } from "@/utils/tags";
import { ActionDoneIndicator } from "../action-done-indicator";
/** A single tag action row — shows "key: current → new", applies the new value, and supports undo back to the old value. */
export function TagAction(_ref) {
  var action = _ref.action,
    tagKey = _ref.tagKey,
    currentValue = _ref.currentValue,
    patientUid = _ref.patientUid,
    reminderId = _ref.reminderId,
    isDone = _ref.isDone,
    onDone = _ref.onDone,
    onUndo = _ref.onUndo,
    onDelete = _ref.onDelete;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var payload = action.payload;
  var newValue = payload.response;
  var storedOldValue = useTagUndoStore(function (s) {
    return s.oldValues[action.id];
  });
  var rememberOldValue = useTagUndoStore(function (s) {
    return s.remember;
  });
  var forgetOldValue = useTagUndoStore(function (s) {
    return s.forget;
  });
  var hasUndo = storedOldValue !== undefined;
  var newText = formatTagValue(newValue);
  var displayOldText = formatTagValue(isDone ? storedOldValue !== null && storedOldValue !== void 0 ? storedOldValue : null : currentValue);
  var showOld = displayOldText.trim() !== "" && displayOldText !== newText;
  var _useUpdateActionState = useUpdateActionState(patientUid, reminderId),
    updateState = _useUpdateActionState.mutate,
    isUpdatingState = _useUpdateActionState.isLoading;
  var _useMutation = useMutation(function () {
      return bulkUpdateTags(Number(patientUid), {
        updated_tags: [{
          tag_id: payload.id,
          response: newValue
        }],
        deleted_tags: []
      });
    }, {
      onSuccess: function onSuccess() {
        var store = usePatientTagsStore.getState();
        store.setServerTags(applyTagUpdate(store.serverTags, payload.id, newValue));
        updateState({
          actionId: action.id,
          state: "done"
        }, {
          onSuccess: onDone
        });
      }
    }),
    applyTag = _useMutation.mutate,
    isApplying = _useMutation.isLoading;
  var _useMutation2 = useMutation(function () {
      return bulkUpdateTags(Number(patientUid), {
        updated_tags: [{
          tag_id: payload.id,
          response: storedOldValue !== null && storedOldValue !== void 0 ? storedOldValue : null
        }],
        deleted_tags: []
      });
    }, {
      onSuccess: function onSuccess() {
        var store = usePatientTagsStore.getState();
        store.setServerTags(applyTagUpdate(store.serverTags, payload.id, storedOldValue !== null && storedOldValue !== void 0 ? storedOldValue : null));
        updateState({
          actionId: action.id,
          state: "pending"
        }, {
          onSuccess: function onSuccess() {
            forgetOldValue(action.id);
            onUndo();
          }
        });
      }
    }),
    revertTag = _useMutation2.mutate,
    isReverting = _useMutation2.isLoading;
  var handleApply = function handleApply() {
    trackEvent(ANALYTICS_EVENTS.TAG_ACTION_APPLY, {
      actionId: action.id
    });
    if (formatTagValue(currentValue).trim() !== "") rememberOldValue(action.id, currentValue);
    applyTag();
  };
  var isLoading = isApplying || isUpdatingState;
  return /*#__PURE__*/React.createElement("div", {
    className: "rounded-sds-200 border px-4 py-3 flex items-center gap-3 ".concat(isDone ? "bg-sds-base-secondary border-transparent" : "bg-sds-base-default border-sds-base-default")
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 flex-1 min-w-0 text-left"
  }, tagKey && /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default flex-shrink-0"
  }, tagKey, ":"), showOld && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small line-through text-sds-base-tertiary bg-sds-base-secondary rounded-sds-full px-3 py-1 min-w-0 truncate"
  }, displayOldText), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faArrowRight,
    className: "w-3.5 h-3.5 text-sds-base-tertiary flex-shrink-0"
  })), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small text-sds-brand-default bg-sds-brand-tertiary rounded-sds-full px-3 py-1 min-w-0 truncate"
  }, newText)), isDone ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActionDoneIndicator, {
    label: t("status.updated", {
      defaultValue: "Updated"
    })
  }), hasUndo && /*#__PURE__*/React.createElement(Button, {
    "data-testid": "tag-action-undo-".concat(action.id),
    variant: "secondary",
    intent: "base",
    size: "sm",
    className: "flex-shrink-0",
    loading: isReverting || isUpdatingState,
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TAG_ACTION_UNDO, {
        actionId: action.id
      });
      revertTag();
    }
  }, t("button.undo", {
    defaultValue: "Undo"
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "tag-action-apply-".concat(action.id),
    intent: "brand",
    variant: "secondary",
    size: "sm",
    className: "flex-shrink-0",
    loading: isLoading,
    onClick: handleApply
  }, t("button.apply", {
    defaultValue: "Apply"
  })), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "tag-action-delete-".concat(action.id),
    "aria-label": t("button.delete", {
      defaultValue: "Delete"
    }),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TAG_ACTION_DELETE, {
        actionId: action.id
      });
      onDelete(action.id);
    },
    className: "w-7 h-7 flex items-center justify-center rounded-sds-100 text-sds-base-secondary hover:bg-sds-urgent-tertiary hover:text-sds-urgent-default transition-colors flex-shrink-0"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faTrash,
    className: "w-3.5 h-3.5"
  }))));
}